import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { Layout, ProtectedRoute } from '@components';
import {
  HomePage,
  SectionsPage,
  CategoryProductsPage,
  ProductsPage,
  ProductPage,
  FilterSetProductsPage,
  CustomSetProductsPage,
  OptionsPage,
  OrdersPage,
  OrderDetailsPage,
  ReviewsPage,
  LoginPage,
  TbankTestPage,
  PushPage,
  BonusesPage,
  SellersPage,
  SellerDetailPage,
  OrderCategoryStatsPage,
  MarketplaceSettingsPage,
  SellerFinancePage,
  SellerSupportChatPage,
  ClientSupportChatPage,
  MethodologyPage,
} from '@pages';
import './App.css';

const theme = createTheme({
  primaryColor: 'dark',
  defaultRadius: 'var(--input-radius)',
  components: {
    TextInput: {
      styles: {
        input: {
          borderRadius: 'var(--input-radius)',
          '&:focus': {
            borderColor: '#000',
          },
        },
      },
    },
    Select: {
      styles: {
        input: {
          borderRadius: 'var(--input-radius)',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Не ретраим 401 ошибки - их обрабатывает axios interceptor
        if (error?.response?.status === 401) {
          return false;
        }
        // Для остальных ошибок делаем 1 попытку
        return failureCount < 1;
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // 10 минут - время хранения кэша в памяти
    },
  },
});

function App() {
  return (
    <MantineProvider theme={theme}>
      <Notifications autoClose={3000} />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Navigate to="/help/ranking" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/:docId"
              element={
                <ProtectedRoute>
                  <MethodologyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="sections" element={<SectionsPage />} />
              <Route path="sections/popular" element={<SectionsPage />} />
              <Route path="sections/popular/categories/:categoryId/products" element={<CategoryProductsPage />} />
              <Route path="sections/without-sections" element={<SectionsPage />} />
              <Route path="sections/without-sections/categories/:categoryId/products" element={<CategoryProductsPage />} />
              <Route path="sections/:sectionId/categories" element={<SectionsPage />} />
              <Route path="sections/:sectionId/categories/:categoryId/products" element={<CategoryProductsPage />} />
              <Route path="categories/:categoryId/products" element={<CategoryProductsPage />} />
              <Route path="sets" element={<FilterSetProductsPage />} />
              <Route path="sets/popular" element={<Navigate to="/sets" replace />} />
              <Route path="sets/new" element={<Navigate to="/sets" replace />} />
              <Route path="sets/discount" element={<Navigate to="/sets" replace />} />
              <Route path="sets/hidden" element={<Navigate to="/sets" replace />} />
              <Route path="sets/out-of-stock" element={<Navigate to="/sets" replace />} />
              <Route path="sets/filter/:id" element={<FilterSetProductsPage />} />
              <Route path="sets/:id" element={<CustomSetProductsPage />} />
              <Route path="products" element={<ProductsPage />} />
              {/* <Route path="products/create" element={<CreateProductPage />} /> */}
              <Route path="products/:productId" element={<ProductPage />} />
              {/* <Route path="products/:productId/edit" element={<EditProductPage />} /> */}
              <Route path="options" element={<OptionsPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/category-stats" element={<OrderCategoryStatsPage />} />
              <Route path="orders/:id" element={<OrderDetailsPage />} />
              <Route path="reviews" element={<ReviewsPage />} />
              <Route path="tbank-test" element={<TbankTestPage />} />
              <Route path="push" element={<PushPage />} />
              <Route path="bonuses" element={<BonusesPage />} />
              <Route path="sellers" element={<SellersPage />} />
              <Route path="sellers/support-chat" element={<SellerSupportChatPage />} />
              <Route path="clients/support-chat" element={<ClientSupportChatPage />} />
              <Route path="sellers/applications/:applicationId" element={<SellerDetailPage />} />
              <Route path="sellers/:sellerId" element={<SellerDetailPage />} />
              <Route path="seller-sales" element={<Navigate to="/seller-finance" replace />} />
              <Route path="seller-finance" element={<SellerFinancePage />} />
              <Route path="marketplace-settings" element={<MarketplaceSettingsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  );
}

export default App;
